import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Welcome } from './pages/Welcome';
import { Login } from './pages/Login';
import { StudioHandoff } from './pages/StudioHandoff';
import { Signup } from './pages/Signup';
import { VerifyEmail } from './pages/VerifyEmail';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Dashboard } from './pages/Dashboard';
import { AccountSettings } from './pages/AccountSettings';
import { StudioLink } from './pages/StudioLink';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminSitesManagement } from './pages/AdminSitesManagement';
import UserDetails from './pages/UserDetails';
import { FinancialDashboard } from './pages/FinancialDashboard';
import { ThemeTest } from './pages/ThemeTest';
import { ThemeManager } from './pages/ThemeManager';
import { AdminTemplateManager } from './pages/AdminTemplateManager';
import { HowToListPage, HowToEditorPage } from './pages/AdminHowTo';
import { HelpGuidesIndex, HelpGuideDetail } from './pages/HelpGuides';
import { SiteManager } from './pages/SiteManager';
import { SiteSettings } from './pages/SiteSettings';
import { SiteAnalytics } from './pages/SiteAnalytics';
import { SiteSubmissions } from './pages/SiteSubmissions';
import { BlogManager } from './pages/BlogManager';
import { BlogPostEditor } from './pages/BlogPostEditor';
import { PageEditor } from './pages/PageEditor';
import { LayoutEditor } from './pages/LayoutEditor';
import { PagePreview } from './pages/PagePreview';
import { WebsitePreview } from './pages/WebsitePreview';
import { WebsiteViewer } from './pages/WebsiteViewer';
import { PublicBlogViewer } from './pages/PublicBlogViewer';
import Pricing from './pages/Pricing';
import Contact from './pages/Contact';
import UpgradeModal from './components/UpgradeModal';
import { MimicUserBanner } from './components/MimicUserBanner';
import { FacebookPixelTracker } from './components/FacebookPixelTracker';
import { LandingPageRoute } from './components/LandingPageRoute';
import { LandingPageProvider } from './contexts/LandingPageContext';
import { PlatformFavicon } from './components/PlatformFavicon';
import { OnboardingGate } from './components/OnboardingGate';
import { OnboardingRoute } from './components/OnboardingRoute';
import { PricingRoute } from './components/PricingRoute';
import { VerifiedUserRoute } from './components/VerifiedUserRoute';
import { VerifiedUserLandingRedirect } from './components/VerifiedUserLandingRedirect';
import { isSubdomain } from './utils/routeHandler';
import './index.css';

// Mimic Banner Wrapper - checks localStorage for mimic state
const MimicBannerWrapper: React.FC = () => {
  const [mimickedUser, setMimickedUser] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);

  useEffect(() => {
    const checkMimicState = () => {
      const originalToken = localStorage.getItem('originalAdminToken');
      const mimickedUserData = localStorage.getItem('mimickedUser');

      if (originalToken && mimickedUserData) {
        try {
          setMimickedUser(JSON.parse(mimickedUserData));
        } catch {
          setMimickedUser(null);
        }
      } else {
        setMimickedUser(null);
      }
    };

    checkMimicState();
    // Listen for storage changes (e.g., from other tabs)
    window.addEventListener('storage', checkMimicState);
    return () => window.removeEventListener('storage', checkMimicState);
  }, []);

  const handleExitMimic = () => {
    const originalToken = localStorage.getItem('originalAdminToken');
    if (originalToken) {
      localStorage.setItem('authToken', originalToken);
      localStorage.removeItem('originalAdminToken');
      localStorage.removeItem('mimickedUser');
      window.location.reload();
    }
  };

  if (!mimickedUser || window.self !== window.top) return null;

  return <MimicUserBanner mimickedUser={mimickedUser} onExit={handleExitMimic} />;
};

// Logged in only (no email check) — e.g. account & subscription, verify page flows
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = localStorage.getItem('user');
  const location = useLocation();
  if (user) return <>{children}</>;
  const redirect = `${location.pathname}${location.search}`;
  return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
};

// Admin Protected Route component
const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('authToken');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is admin from JWT token
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.isAdmin) {
      return <>{children}</>;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  } catch {
    // Invalid token
    return <Navigate to="/login" replace />;
  }
};

// Public Route component (redirect to dashboard/onboarding or verify if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = localStorage.getItem('user');
  if (!user) return <>{children}</>;
  try {
    const u = JSON.parse(user) as { emailVerified?: boolean; isAdmin?: boolean };
    if (u.isAdmin || u.emailVerified !== false) {
      return <VerifiedUserLandingRedirect user={u} />;
    }
    return <Navigate to="/verify-email" replace />;
  } catch {
    return <>{children}</>;
  }
};

/** Logged in and email verified (or admin) — app "building" surfaces */
const BuildingRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = localStorage.getItem('user');
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  try {
    const u = JSON.parse(user) as { emailVerified?: boolean; isAdmin?: boolean };
    if (u.isAdmin || u.emailVerified !== false) {
      return <OnboardingGate>{children}</OnboardingGate>;
    }
    return <Navigate to="/verify-email" replace />;
  } catch {
    return <Navigate to="/login" replace />;
  }
};

function App() {
  // Check if we're on a subdomain (website viewer) or main domain (admin interface)
  const isOnSubdomain = isSubdomain();

  console.log('App routing decision:', {
    hostname: window.location.hostname,
    isOnSubdomain,
    willRenderWebsiteViewer: isOnSubdomain,
  });

  if (isOnSubdomain) {
    // Render website viewer for subdomains
    return (
      <Router>
        <div className="App">
          <Routes>
            {/* Blog routes - must be before the catch-all */}
            <Route path="/blog" element={<PublicBlogViewer />} />
            <Route path="/blog/tag/:tag" element={<PublicBlogViewer />} />
            <Route path="/blog/:slug" element={<PublicBlogViewer />} />
            {/* Regular page routes */}
            <Route path="/" element={<WebsiteViewer />} />
            <Route path="/:slug" element={<WebsiteViewer />} />
            <Route path="*" element={<WebsiteViewer />} />
          </Routes>
        </div>
      </Router>
    );
  }

  // Render admin interface for main domain
  return (
    <Router>
      <PlatformFavicon>
        <LandingPageProvider>
          <div className="App">
            <FacebookPixelTracker />
            <MimicBannerWrapper />
            <UpgradeModal />
            <Routes>
              {/* Public Routes — when an admin has designated a landing site,
                these non-app paths render its pages; otherwise they fall
                back to the existing static components. */}
              <Route
                path="/"
                element={
                  <LandingPageRoute
                    slug=""
                    fallback={
                      <PublicRoute>
                        <Welcome />
                      </PublicRoute>
                    }
                  />
                }
              />
              <Route path="/pricing" element={<PricingRoute />} />
              <Route
                path="/contact"
                element={<LandingPageRoute slug="contact" fallback={<Contact />} />}
              />
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route path="/auth/studio-handoff" element={<StudioHandoff />} />
              <Route
                path="/signup"
                element={
                  <PublicRoute>
                    <Signup />
                  </PublicRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <PublicRoute>
                    <ResetPassword />
                  </PublicRoute>
                }
              />
              <Route path="/verify-email" element={<VerifyEmail />} />

              <Route
                path="/onboarding"
                element={
                  <VerifiedUserRoute>
                    <OnboardingRoute>
                      <Pricing forceOnboardingFlow />
                    </OnboardingRoute>
                  </VerifiedUserRoute>
                }
              />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <BuildingRoute>
                    <Dashboard />
                  </BuildingRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <AccountSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/studiolink/:id"
                element={
                  <ProtectedRoute>
                    <StudioLink />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/users/:userId"
                element={
                  <AdminProtectedRoute>
                    <UserDetails />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/financials"
                element={
                  <AdminProtectedRoute>
                    <FinancialDashboard />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/sites"
                element={
                  <AdminProtectedRoute>
                    <AdminSitesManagement />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/themes"
                element={
                  <AdminProtectedRoute>
                    <ThemeManager />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/templates"
                element={
                  <AdminProtectedRoute>
                    <AdminTemplateManager />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/howto"
                element={
                  <AdminProtectedRoute>
                    <HowToListPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/howto/new"
                element={
                  <AdminProtectedRoute>
                    <HowToEditorPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/howto/:id/edit"
                element={
                  <AdminProtectedRoute>
                    <HowToEditorPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/help"
                element={
                  <ProtectedRoute>
                    <HelpGuidesIndex />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/help/:slug"
                element={
                  <ProtectedRoute>
                    <HelpGuideDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sites/:siteId"
                element={
                  <BuildingRoute>
                    <SiteManager />
                  </BuildingRoute>
                }
              />
              <Route
                path="/sites/:siteId/settings"
                element={
                  <BuildingRoute>
                    <SiteSettings />
                  </BuildingRoute>
                }
              />
              <Route
                path="/sites/:siteId/analytics"
                element={
                  <BuildingRoute>
                    <SiteAnalytics />
                  </BuildingRoute>
                }
              />
              <Route
                path="/sites/:siteId/submissions"
                element={
                  <BuildingRoute>
                    <SiteSubmissions />
                  </BuildingRoute>
                }
              />
              <Route
                path="/sites/:siteId/blog"
                element={
                  <BuildingRoute>
                    <BlogManager />
                  </BuildingRoute>
                }
              />
              <Route
                path="/sites/:siteId/blog/posts/:postId"
                element={
                  <BuildingRoute>
                    <BlogPostEditor />
                  </BuildingRoute>
                }
              />
              <Route
                path="/sites/:siteId/pages/:pageId/editor"
                element={
                  <BuildingRoute>
                    <PageEditor />
                  </BuildingRoute>
                }
              />
              <Route
                path="/sites/:siteId/layouts/:layoutId"
                element={
                  <BuildingRoute>
                    <LayoutEditor />
                  </BuildingRoute>
                }
              />
              <Route
                path="/sites/:siteId/preview"
                element={
                  <BuildingRoute>
                    <WebsitePreview />
                  </BuildingRoute>
                }
              />
              <Route
                path="/sites/:siteId/preview/page/:slug"
                element={
                  <BuildingRoute>
                    <WebsitePreview />
                  </BuildingRoute>
                }
              />

              {/* Public Preview Route - No authentication required */}
              <Route path="/preview/:siteId/:pageId" element={<PagePreview />} />

              {/* Theme Test Route */}
              <Route path="/theme-test" element={<ThemeTest />} />

              {/* Catch-all: resolve path against the landing site; redirect home
                only after loading finishes and the slug is missing. */}
              <Route
                path="*"
                element={
                  <LandingPageRoute fallback={<Navigate to="/" replace />} loadingFallback={null} />
                }
              />
            </Routes>
          </div>
        </LandingPageProvider>
      </PlatformFavicon>
    </Router>
  );
}

export default App;
